import _ from 'lodash'
import React, { useMemo, useRef } from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormGroup from '@mui/material/FormGroup'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import WarningIcon from '@mui/icons-material/Warning'

import colors from '@/assets/scss/_themes-vars.module.scss'

import CheckboxField from '@/components/formik/fields/CheckboxField'

export type DeleteStaffConfirmationDialogProps = {
  isDoctor?: boolean
  name: string
  surname: string
  isDeleting?: boolean
  open: boolean
  onSubmit: (values: { associatedData: string[] }) => void
  onClose: () => void
}

function DeleteStaffConfirmationDialog(props: DeleteStaffConfirmationDialogProps) {
  const { isDoctor = false, name, surname, isDeleting = false, open, onSubmit, onClose } = props
  const { t } = useTranslation()

  const formRef = useRef<FormikProps<{ associatedData: string[] }>>(null)

  const associatedDeletions = useMemo(
    () => [
      { label: 'consultations', value: 'consultation' },
      { label: 'patients', value: 'patient' },
    ],
    []
  )

  const handleDelete = () => {
    formRef.current?.submitForm()
  }

  return (
    <Dialog onClose={onClose} maxWidth="sm" open={open} PaperProps={{ sx: { minWidth: '500px' } }}>
      <DialogTitle>
        <Stack alignItems="center" spacing={2}>
          <Avatar sx={{ width: '60px', height: '60px', bgcolor: colors.red100 }}>
            <WarningIcon color="error" fontSize="large" />
          </Avatar>
          <Stack alignItems="center">
            <Typography variant="h6" component="div" sx={{ fontSize: '24px', textAlign: 'center' }}>
              {t('page.admin.staffs.deleteStaffConfirmationDialog.title', { context: isDoctor ? 'doctor' : 'pds', staff: `${name} ${surname}` })}
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Formik
          innerRef={formRef}
          initialValues={{
            associatedData: _.map(associatedDeletions, 'value'),
          }}
          onSubmit={onSubmit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit}>
              <Stack spacing={1}>
                <Typography variant="body1" component="div" sx={{ textAlign: 'justify' }}>
                  {t('page.admin.staffs.deleteStaffConfirmationDialog.message')}
                </Typography>
                <FormGroup>
                  {_.map(associatedDeletions, ({ label, value }) => (
                    <CheckboxField key={value} label={label} name="associatedData" value={value} />
                  ))}
                </FormGroup>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        <Button variant="neutral" onClick={onClose}>
          {t('common:button.cancel.label')}
        </Button>
        <LoadingButton data-testid="staff-delete-confirm" color="error" disableElevation loading={isDeleting} onClick={handleDelete} variant="contained">
          {t('common:button.confirm.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteStaffConfirmationDialog
